/**
 * @file ProductGroupItemRow.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The item window's Product group row (Cam, 28 Oct 2026 — O1):
 *              the group the item is in (a link that opens it in Manage
 *              Inventory → Product Groups), its variant / additive label, and
 *              "Change group…" — move it to another group, take it out, or
 *              make a new group for it — through GET / PUT
 *              /api/product-groups/item/:itemId (and POST / for a new one).
 *              Says "Not ready" before migrations/product_groups.sql; greyed
 *              with why without 'Edit inventory items'. Renders nothing for a
 *              caller the groups are not open to (a rep).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { NOT_READY_REASON, packLabel, errorOf, groupNameProblem } from '../utils/productGroups';
import './ProductGroups.css';

const NEW_GROUP = '__new__';

function ChangeGroupWindow({ itemId, current, additive, onClose, onSaved }) {
  const [groups, setGroups] = useState(null);
  const [choice, setChoice] = useState(current ? current.id : null);
  const [newName, setNewName] = useState('');
  const [label, setLabel] = useState(additive || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useSlideOutButton([saving, choice]);

  useEffect(() => {
    fetch(`${API_URL}/api/product-groups`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { groups: [] }))
      .then(d => setGroups(d.groups || []))
      .catch(() => setGroups([]));
  }, []);

  const options = useMemo(() => [
    { value: null, label: '— No group —', sticky: true },
    { value: NEW_GROUP, label: '+ A new group…', sticky: true },
    ...(groups || []).map(g => ({
      value: g.id,
      label: g.name,
      tag: (g.packs || []).map(packLabel).join(', '),
      searchText: `${g.name} ${g.category || ''} ${g.members.map(m => m.item_code).join(' ')}`
    }))
  ], [groups]);

  const problem = choice === NEW_GROUP ? groupNameProblem(newName) : null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      let res;
      if (choice === NEW_GROUP) {
        res = await fetch(`${API_URL}/api/product-groups`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName, items: [{ itemId, additive: label || null }] })
        });
      } else {
        res = await fetch(`${API_URL}/api/product-groups/item/${itemId}`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId: choice, additive: choice ? (label || null) : null })
        });
      }
      if (!res.ok) throw new Error(await errorOf(res, 'Could not change the group'));
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" className="product-group" title="Change product group"
      footer={
        <div className="buttons slideout pg-footer">
          <div className="pg-footer-gap" />
          <div>
            <button className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" data-write-action onClick={save} disabled={saving || !!problem || groups === null}
              title={problem || 'Save'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }>
      <div className="pg-window">
        <label className="pg-field">
          <span>Group</span>
          <SearchableDropdown options={options} value={choice} onChange={setChoice}
            placeholder={groups === null ? 'Loading groups…' : 'Choose a group'} searchPlaceholder="Group name or item code" />
        </label>
        {choice === NEW_GROUP && (
          <label className="pg-field">
            <span>New group's name</span>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Supreme Rearer 18 in totes" />
          </label>
        )}
        {choice !== null && (
          <label className="pg-field">
            <span>Variant / additive (blank = the plain product)</span>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. + Deccox 833" />
          </label>
        )}
        <p className="pg-hint">One group per product per pack. An item is in one group at most — choosing another moves it.</p>
        {error && <p className="pg-hint" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

function ProductGroupItemRow({ itemId, onOpenGroup = null }) {
  const [state, setState] = useState(null);
  const [changing, setChanging] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    try {
      const res = await fetch(`${API_URL}/api/product-groups/item/${itemId}`, { credentials: 'include' });
      if (res.status === 403) { setState({ hidden: true }); return; }
      setState(res.ok ? await res.json() : { error: await errorOf(res, 'Could not read the group') });
    } catch {
      setState({ error: 'Could not read the group' });
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  if (!state || state.hidden) return null;
  const ready = state.ready !== false;
  const g = state.group;
  const block = !ready ? (state.reason || NOT_READY_REASON) : !state.can_edit ? "Changing product groups needs 'Edit inventory items'" : null;

  return (
    <div className="detail-row pg-item-row">
      <div className="detail-group">
        <label>Product group</label>
        <div className="detail-value pg-item-value">
          {!ready ? <span className="pg-muted">{block}</span>
            : state.error ? <span className="pg-muted">{state.error}</span>
              : g ? (
                <>
                  {onOpenGroup
                    ? <button type="button" className="pg-link" onClick={() => onOpenGroup(g.id)} title="Open the group">{g.name}</button>
                    : <span>{g.name}</span>}
                  {g.is_archived && <span className="archived-badge">Archived</span>}
                  <span className="pg-muted">{state.additive ? state.additive : 'Plain'} · {g.member_count} item{g.member_count === 1 ? '' : 's'}</span>
                </>
              ) : <span className="pg-muted">In no group</span>}
          {ready && (
            <button className="btn-slideout" data-write-action onClick={() => setChanging(true)} disabled={!!block}
              title={block || 'Move it to another group, take it out, or make a new group for it'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">stacks</span></div>
              <span className="btn-label">Change group…</span>
            </button>
          )}
        </div>
      </div>
      {changing && (
        <ChangeGroupWindow itemId={itemId} current={g} additive={state.additive}
          onClose={() => setChanging(false)} onSaved={() => { setChanging(false); load(); }} />
      )}
    </div>
  );
}

export default ProductGroupItemRow;
